import React from 'react';
import { Alert, Button, Card, Descriptions, Form, Input, Space, Typography, message } from 'antd';
import { HomeOutlined, LoginOutlined, ReloadOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { AXIOS_INSTANCE } from '@/shared/api/mutator/custom-instance';
import { useAuthStore } from '@/modules/auth';

const { Title, Paragraph, Text } = Typography;

type LicenseStatusResponse = {
    enabled: boolean;
    valid: boolean;
    overLimit: boolean;
    operational: boolean;
    reference?: string;
    status?: string;
    maxUsers?: number;
    heartbeatGraceDays?: number;
    licensedBranches?: string;
    currentUsers?: number;
    expireDate?: string;
    clientName?: string;
    enforcementReason?: string;
    lastError?: string;
    lastPullAt?: string;
    lastReportAt?: string;
    lastVerifiedAt?: string;
    heartbeatIssuedAt?: string;
    heartbeatValidUntil?: string;
};

type LicenseSetupResponse = {
    success: boolean;
    message: string;
    status: LicenseStatusResponse;
    reference?: string;
    keyId?: string;
};

const LicenseSetupView: React.FC = () => {
    const [form] = Form.useForm<{ bootstrapToken: string }>();
    const [heartbeatForm] = Form.useForm<{ heartbeatToken: string }>();
    const navigate = useNavigate();
    const user = useAuthStore((state) => state.user);

    const statusQuery = useQuery({
        queryKey: ['license-status'],
        queryFn: async () => (await AXIOS_INSTANCE.get<LicenseStatusResponse>('/api/license/status')).data,
        refetchInterval: 30000,
    });

    const setupMutation = useMutation({
        mutationFn: async (bootstrapToken: string) => (
            await AXIOS_INSTANCE.post<LicenseSetupResponse>('/api/license/setup', { bootstrapToken })
        ).data,
        onSuccess: (response) => {
            message.success(response.message || 'License setup completed');
            form.resetFields();
            statusQuery.refetch();
        },
        onError: (error: any) => {
            const responseMessage = error?.response?.data?.message;
            message.error(responseMessage || 'License setup failed');
        },
    });

    const refreshMutation = useMutation({
        mutationFn: async () => (await AXIOS_INSTANCE.post<LicenseStatusResponse>('/api/license/refresh')).data,
        onSuccess: () => {
            message.success('License status refreshed');
            statusQuery.refetch();
        },
        onError: (error: any) => {
            const responseMessage = error?.response?.data?.message;
            message.error(responseMessage || 'Refresh failed');
        },
    });

    const reportMutation = useMutation({
        mutationFn: async () => (await AXIOS_INSTANCE.post<LicenseStatusResponse>('/api/license/report')).data,
        onSuccess: () => {
            message.success('Boot reported to license server');
            statusQuery.refetch();
        },
        onError: (error: any) => {
            const responseMessage = error?.response?.data?.message;
            message.error(responseMessage || 'Report failed');
        },
    });

    const heartbeatMutation = useMutation({
        mutationFn: async (heartbeatToken: string) => (
            await AXIOS_INSTANCE.post<LicenseStatusResponse>('/api/license/heartbeat/apply', { heartbeatToken })
        ).data,
        onSuccess: () => {
            message.success('Offline heartbeat token applied');
            heartbeatForm.resetFields();
            statusQuery.refetch();
        },
        onError: (error: any) => {
            const responseMessage = error?.response?.data?.message;
            message.error(responseMessage || 'Failed to apply heartbeat token');
        },
    });

    const status = statusQuery.data;
    const alertDescription = status?.operational
        ? (status?.lastVerifiedAt
            ? `This deployment is licensed and was last verified at ${status.lastVerifiedAt}.${status?.heartbeatValidUntil ? ` Offline heartbeat valid until ${status.heartbeatValidUntil}.` : ''}`
            : (status?.heartbeatValidUntil
                ? `This deployment is licensed and operating with an offline heartbeat valid until ${status.heartbeatValidUntil}.`
                : 'This deployment is licensed and operational.'))
        : (status?.lastError || status?.enforcementReason || 'Configure the bootstrap token from scale-management to activate this deployment.');

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <Title level={2} style={{ margin: 0 }}>
                    <SafetyCertificateOutlined className="mr-2" />License Setup & Status
                </Title>
                <Space>
                    <Button icon={<HomeOutlined />} onClick={() => navigate('/')}>
                        {user ? 'Open Application' : 'Home'}
                    </Button>
                    {!user && (
                        <Button icon={<LoginOutlined />} onClick={() => navigate('/login')}>
                            Login
                        </Button>
                    )}
                    <Button icon={<ReloadOutlined />} onClick={() => statusQuery.refetch()} loading={statusQuery.isFetching}>
                        Reload
                    </Button>
                </Space>
            </div>

            <Alert
                type={status?.operational ? 'success' : 'warning'}
                showIcon
                message={status?.operational ? 'License is active' : 'License setup required'}
                description={alertDescription}
            />

            <Card>
                <Paragraph>
                    Paste the one-time bootstrap token generated from the management portal to install this deployment's client key and pull the signed license.
                </Paragraph>
                <Form form={form} layout="vertical" onFinish={(values) => setupMutation.mutate(values.bootstrapToken)}>
                    <Form.Item
                        name="bootstrapToken"
                        label="Bootstrap Token"
                        rules={[{ required: true, message: 'Bootstrap token is required' }]}
                    >
                        <Input.TextArea rows={4} placeholder="BOOT-..." />
                    </Form.Item>
                    <Space>
                        <Button type="primary" htmlType="submit" loading={setupMutation.isPending}>
                            Complete Setup
                        </Button>
                        <Button onClick={() => refreshMutation.mutate()} loading={refreshMutation.isPending}>
                            Refresh From Server
                        </Button>
                        <Button onClick={() => reportMutation.mutate()} loading={reportMutation.isPending}>
                            Report to Server
                        </Button>
                    </Space>
                </Form>
            </Card>

            <Card>
                <Paragraph>
                    If the license server is unreachable, paste a signed offline heartbeat token from the management portal to keep this deployment operational until communication is restored.
                </Paragraph>
                <Form form={heartbeatForm} layout="vertical" onFinish={(values) => heartbeatMutation.mutate(values.heartbeatToken)}>
                    <Form.Item
                        name="heartbeatToken"
                        label="Offline Heartbeat Token"
                        rules={[{ required: true, message: 'Heartbeat token is required' }]}
                    >
                        <Input.TextArea rows={4} placeholder="Paste offline heartbeat token" />
                    </Form.Item>
                    <Space>
                        <Button type="primary" htmlType="submit" loading={heartbeatMutation.isPending}>
                            Apply Heartbeat Token
                        </Button>
                    </Space>
                </Form>
            </Card>

            <Card title="Current License State">
                <Descriptions bordered size="small" column={2}>
                    <Descriptions.Item label="Enabled">{String(status?.enabled ?? false)}</Descriptions.Item>
                    <Descriptions.Item label="Valid">{String(status?.valid ?? false)}</Descriptions.Item>
                    <Descriptions.Item label="Operational">{String(status?.operational ?? false)}</Descriptions.Item>
                    <Descriptions.Item label="Reference">{status?.reference || '-'}</Descriptions.Item>
                    <Descriptions.Item label="Status">{status?.status || '-'}</Descriptions.Item>
                    <Descriptions.Item label="Client">{status?.clientName || '-'}</Descriptions.Item>
                    <Descriptions.Item label="Users">{`${status?.currentUsers ?? 0} / ${status?.maxUsers ?? '-'}`}</Descriptions.Item>
                    <Descriptions.Item label="Heartbeat Days">{status?.heartbeatGraceDays ?? '-'}</Descriptions.Item>
                    <Descriptions.Item label="Licensed Branches" span={2}>{status?.licensedBranches || '-'}</Descriptions.Item>
                    <Descriptions.Item label="Expires">{status?.expireDate || '-'}</Descriptions.Item>
                    <Descriptions.Item label="Over Limit">{String(status?.overLimit ?? false)}</Descriptions.Item>
                    <Descriptions.Item label="Last Pull">{status?.lastPullAt || '-'}</Descriptions.Item>
                    <Descriptions.Item label="Last Verified">{status?.lastVerifiedAt || '-'}</Descriptions.Item>
                    <Descriptions.Item label="Heartbeat Issued">{status?.heartbeatIssuedAt || '-'}</Descriptions.Item>
                    <Descriptions.Item label="Heartbeat Valid Until">{status?.heartbeatValidUntil || '-'}</Descriptions.Item>
                    <Descriptions.Item label="Reason" span={2}>{status?.enforcementReason || '-'}</Descriptions.Item>
                    <Descriptions.Item label="Last Error" span={2}>{status?.lastError || '-'}</Descriptions.Item>
                </Descriptions>
                {!status?.operational && (
                    <Text type="secondary" className="block mt-3">
                        Until this page shows a valid license, normal protected APIs remain blocked.
                    </Text>
                )}
            </Card>
        </div>
    );
};

export default LicenseSetupView;
